import React, { useRef, useState, useEffect, useMemo } from "react";
import jsyaml from "js-yaml";
import tractumJson from "./data/tractum-example.json";

export default function TractumSvgEditor({ initialYaml, onClose }) {
  const svgRef = useRef(null);
  // --- Viewport State ---
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 100, y: 100 });
  const [panStart, setPanStart] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [dragOrigin, setDragOrigin] = useState(null); // This will hold the initial drag position

  const [dragStartPositions, setDragStartPositions] = useState(null);
  const [svgWidth, setSvgWidth] = useState(0); // State for the width of the SVG
   const [svgHeight, setSvgHeight] = useState(0); // State for the width of the SVG

  


  // --- Data State ---
  const [nodes, setNodes] = useState([]); // Array containing nodes, edges, and areas
  const [selectedId, setSelectedId] = useState(null);
  const [yamlText, setYamlText] = useState("");
  const [message, setMessage] = useState("Ready");

  // --- Interaction State ---
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [edgeSource, setEdgeSource] = useState(null);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionBox, setSelectionBox] = useState(null); 
  // { x, y, width, height }
  const [selectionStart, setSelectionStart] = useState(null);


  // --- 1. CORE SYNC: State to YAML ---
  const syncStateToYaml = (currentNodes) => {
    try {
      const map = {
        nodes: currentNodes.filter((n) => n.type !== "edge" && n.type !== "area"),
        edges: currentNodes.filter((n) => n.type === "edge"),
        areas: currentNodes.filter((n) => n.type === "area"),
      };
      const yaml = jsyaml.dump({ "tractum-map": map });
      setYamlText(yaml);
    } catch (e) {
      console.error("YAML Sync Error", e);
    }
  };

  

  // --- 2. CORE SYNC: YAML to State ---
  const handleYamlChange = (val) => {
    setYamlText(val);
    try {
      const parsed = jsyaml.load(val);
      if (parsed && parsed["tractum-map"]) {
        const map = parsed["tractum-map"];
        const n = (map.nodes || []).map(node => ({ ...node, type: node.type || "system", position: node.position || { x: 0, y: 0 } }));
        const e = (map.edges || []).map(edge => ({ ...edge, type: "edge" }));
        const a = (map.areas || []).map(area => ({ ...area, type: "area" }));
        setNodes([...n, ...e, ...a]);
        setMessage("YAML Applied ✅");
      }
    } catch (e) {
      setMessage("YAML Syntax Error ❌");
    }
  };

    // --- 1. STATUS COLOR SCHEMATIC ---
  const getStatusColor = (status) => {
    const map = {
      running: "#00d4ff", // Cyan
      ok: "#52ff7a",      // Green
      done: "#52ff7a",    
      pending: "#ffcc00", // Yellow
      open: "#ff5252",    // Red
      warning: "#ff5252"
    };
    return map[status?.toLowerCase()] || "#888";
  };

  // --- 2. AUTO-LAYOUT (Org Chart / Tree Logic) ---
  const applyAutoLayout = () => {
    const spacingX = 220;
    const spacingY = 150;
    const newNodes = [...nodes];
    
    // Simple Vertical Tree Layout logic
    const systems = newNodes.filter(n => n.type === 'system');
    systems.forEach((sys, sIdx) => {
      sys.position = { x: sIdx * 500 + 100, y: 50 };
      
      const children = newNodes.filter(n => 
        newNodes.some(e => e.type === 'edge' && e.from === sys.id && e.to === n.id)
      );
      
      children.forEach((child, cIdx) => {
        child.position = { x: sys.position.x + (cIdx - (children.length-1)/2) * spacingX, y: sys.position.y + spacingY };
        
        // Grandchildren (Issues/Checklists)
        const gChildren = newNodes.filter(n => 
          newNodes.some(e => e.type === 'edge' && e.from === child.id && e.to === n.id)
        );
        gChildren.forEach((g, gIdx) => {
          g.position = { x: child.position.x + (gIdx - (gChildren.length-1)/2) * 120, y: child.position.y + spacingY };
        });
      });
    });
    setNodes(newNodes);
    setMessage("Tree Layout Applied");
  };

  // --- 3. NODE OPERATIONS (Add / Copy / Delete) ---
  const addNode = (type = "system") => {
    const id = `${type.substring(0,3)}-${Date.now().toString().slice(-4)}`;
    const newNode = {
      id, type, label: `New ${type}`,
      position: { x: 200, y: 200 },
      dimension: { r: 20 },
      semantic: { status: "pending" },
      style: { color: type === "issue" ? "#ff5252" : "#3399ff" }
    };
    setNodes([...nodes, newNode]);
    setSelectedId(id);
  };

  const copyNode = () => {
    const target = nodes.find(n => n.id === selectedId);
    if (!target || target.type === 'edge') return;
    const id = `${target.id}-copy`;
    const newNode = { ...target, id, position: { x: target.position.x + 40, y: target.position.y + 40 } };
    setNodes([...nodes, newNode]);
    setSelectedId(id);
  };

  const deleteNode = () => {
    setNodes(nodes.filter(n => n.id !== selectedId && n.from !== selectedId && n.to !== selectedId));
    setSelectedId(null);
  };

  // --- 4. DATA LOADING ---
  const loadData = (dataObj) => {
    const map = dataObj["tractum-map"];
    const n = (map.nodes || []).map(node => ({ ...node, type: node.type || "node", position: node.position || { x: 0, y: 0 } }));
    const e = (map.edges || []).map(edge => ({ ...edge, type: "edge" }));
    const a = (map.areas || []).map(area => ({ ...area, type: "area" }));
    setNodes([...n, ...e, ...a]);
  };

  // --- 4. INTERACTION HANDLERS ---
  const getSvgCoords = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - offset.x) / zoom,
      y: (e.clientY - rect.top - offset.y) / zoom,
    };
  };

const handleMouseMove = (e) => {
  const coords = getSvgCoords(e);
  setMousePos(coords);

  // Handle Selection Box: If the user is selecting, update the box size
  if (selectionStart) {
    const curr = getSvgCoords(e); // Get the current mouse position
    setSelectionBox({
      x: Math.min(selectionStart.x, curr.x),
      y: Math.min(selectionStart.y, curr.y),
      width: Math.abs(curr.x - selectionStart.x),
      height: Math.abs(curr.y - selectionStart.y)
    });
  } 
  // Handle Panning: If the user is panning, update the offset to move the canvas
  else if (panStart) {
    setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  } 
  // Handle Node Dragging: If the user is dragging a node, update its position
  else if (draggingId) {
    setNodes((prev) =>
      prev.map((n) =>
        n.id === draggingId
          ? { ...n, position: { x: coords.x - dragOffset.x, y: coords.y - dragOffset.y } }
          : n
      )
    );
  }
  // No action if neither selection, panning, nor dragging
};

const handleMouseDown = (e) => {
  // Check if the user clicked the canvas (not a node)
  if (e.target === svgRef.current) {
    // Initialize panStart when clicking the canvas to start panning
    const coords = getSvgCoords(e);
    setPanStart(coords);
  }

  // Handle Selection Box
  if (!selectionStart) {
    const coords = getSvgCoords(e);
    setSelectionStart(coords);
    setSelectionBox({ x: coords.x, y: coords.y, width: 0, height: 0 });
  }
};



  const handleMouseUp = () => {
    if (selectionBox) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        nodes.forEach((n) => {
          if (!n.position) return;
          const { x, y } = n.position;
          const inside =
            x >= selectionBox.x &&
            x <= selectionBox.x + selectionBox.width &&
            y >= selectionBox.y &&
            y <= selectionBox.y + selectionBox.height;

          if (inside) next.add(n.id);
        });
        return next;
      });
    }
    if (draggingId) syncStateToYaml(nodes); // Sync code when drag stops
    setDraggingId(null);
    setPanStart(null);
    setSelectionBox(null);
    setSelectionStart(null);
    setDraggingId(null);
  };
  // --- 5. INITIAL LOAD ---

const handleWheel = (e) => {
  e.preventDefault(); // Stop the whole page from scrolling

  const scaleFactor = 1.1; // The zoom scale factor
  const zoomIn = e.deltaY < 0;
  let newZoom = zoomIn ? zoom * scaleFactor : zoom / scaleFactor;

  // Restrict zoom levels (adjust as necessary)
  if (newZoom < 0.1 || newZoom > 5) return;

  // Get the mouse position relative to the canvas
  const rect = svgRef.current.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  // Calculate new offset based on zoom level
  setOffset((prev) => {
    const newOffset = {
      x: mouseX - (mouseX - prev.x) * (newZoom / zoom),
      y: mouseY - (mouseY - prev.y) * (newZoom / zoom),
    };
    return newOffset;
  });

  // Apply new zoom level
  setZoom(newZoom);
};




  // --- 4. Export logic ---
  const handleExport = () => {
    const map = {
      nodes: nodes.filter((n) => n.type === "node"),
      edges: nodes.filter((n) => n.type === "edge"),
      areas: nodes.filter((n) => n.type === "area"),
    };
    const yaml = jsyaml.dump({ "tractum-map": map });
    setYamlText(yaml);
    setMessage("Exported to YAML");
  };

  // --- 5. INITIAL LOAD ---
  useEffect(() => {
    const text = initialYaml || jsyaml.dump(tractumJson);
    handleYamlChange(text);
  }, []);
    useEffect(() => {
    if (svgRef.current) {
      setSvgWidth(svgRef.current.offsetWidth); // Get the width of the SVG container
    }
  }, []);

  return (
    <div className="tractum-editor-wrapper" style={{ display: "flex", flexDirection: "column", height: "100%", background: "#050505", color: "#eee" }}>
      
      {/* ----------------- CSS ANIMATIONS ----------------- */}
      <style>{`
        @keyframes pulse-red {
          0% { r: 22; opacity: 0.6; stroke-width: 2; }
          50% { r: 28; opacity: 0; stroke-width: 10; }
          100% { r: 22; opacity: 0; stroke-width: 2; }
        }
        .pulse-critical { animation: pulse-red 2s infinite; }
      `}</style>

      <div style={{ display: "flex", gap: "10px", padding: "10px", background: "var(--bg-panel)", borderBottom: "1px solid #333", alignItems: "center" }}>
        <button onClick={onClose} style={{ background: "#c0392b", color: "#fff", border: "none", padding: "5px 10px", borderRadius: "4px" }}>✕</button>
        <button onClick={applyAutoLayout} className="pagination-btn">📐 Auto-Layout</button>
        <div style={{ width: "1px", height: "20px", background: "#444" }} />
        <button onClick={() => addNode("system")} className="pagination-btn">➕ System</button>
        <button onClick={() => addNode("issue")} className="pagination-btn">➕ Issue</button>
        <button onClick={copyNode} className="pagination-btn" disabled={!selectedId}>📋 Copy</button>
        <button onClick={deleteNode} className="pagination-btn" disabled={!selectedId} style={{ color: "#ff5252" }}>🗑 Delete</button>
        <span style={{ marginLeft: "auto", fontSize: "11px", color: "var(--accent-secondary)" }}>{message}</span>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        
        {/* LEFT: YAML EDITOR */}
        <div style={{ width: "350px", display: "flex", flexDirection: "column", borderRight: "1px solid #333", background: "#0a0a0a" }}>
          <div style={{ padding: "10px", fontSize: "10px", background: "#111", color: "var(--accent-secondary)", fontWeight: "bold" }}>REALTIME YAML EDITOR</div>
          <textarea
            value={yamlText}
            onChange={(e) => handleYamlChange(e.target.value)}
            style={{ 
              flex: 1, background: "transparent", color: "#00ff9d", border: "none", 
              padding: "15px", fontSize: "11px", fontFamily: "monospace", outline: "none", resize: "none" 
            }}
          />
        </div>

        {/* CENTER: CANVAS */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#000" }}
        onWheel={handleWheel}>
          <svg 
            ref={svgRef} width="100%" height="100%" 
            onMouseMove={handleMouseMove}
            
            onMouseUp={handleMouseUp}
            onMouseDown={handleMouseDown}
            style={{ cursor: panStart ? "grabbing" : "crosshair" }}
            viewBox={`${offset.x} ${offset.y} ${svgWidth} ${svgHeight}`} // Dynamically update viewBox to fit the zoom
          >
              {/* Optional: Add a Background Grid that scales */}
            <defs>
              <pattern id="grid" width={50 * zoom} height={50 * zoom} patternUnits="userSpaceOnUse">
                <path d={`M ${50 * zoom} 0 L 0 0 0 ${50 * zoom}`} fill="none" stroke="#111" strokeWidth="1" />
              </pattern>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="28" refY="5" orient="auto">
                <path d="M0,0 L10,5 L0,10 Z" fill="#333" />
              </marker>
            </defs>
                           {selectionBox && (
                <rect
                  x={selectionBox.x}
                  y={selectionBox.y}
                  width={selectionBox.width}
                  height={selectionBox.height}
                  fill="rgba(100,150,255,0.15)"
                  stroke="rgba(100,150,255,0.6)"
                  strokeDasharray="4"
                />
              )}

              <g transform={`translate(${offset.x}, ${offset.y}) scale(${zoom})`}>

              {/* EDGES */}
              {nodes.filter(n => n.type === 'edge').map(e => {
                const from = nodes.find(n => n.id === e.meta?.from || e.from);
                const to = nodes.find(n => n.id === e.meta?.to || e.to);
                if (!from?.position || !to?.position) return null;
                return (
                  <line key={e.id} x1={from.position.x} y1={from.position.y} x2={to.position.x} y2={to.position.y} 
                    stroke={selectedId === e.id ? "var(--accent)" : "#333"} strokeWidth="2" markerEnd="url(#arrow)" />
                );
              })}

              {/* NODES */}
              {nodes.filter(n => n.type !== 'edge' && n.type !== 'area').map(n => {
                const isCritical = n.semantic?.status === "open" || n.semantic?.status === "warning";
                const statusColor = getStatusColor(n.semantic?.status);
                const isSelected = selectedIds.has(n.id);
                return (
                  <g key={n.id}                  
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const coords = getSvgCoords(e);

                      const nextSelected = new Set(selectedIds);
                      if (e.shiftKey) {
                        nextSelected.has(n.id)
                          ? nextSelected.delete(n.id)
                          : nextSelected.add(n.id);
                      } else {
                        nextSelected.clear();
                        nextSelected.add(n.id);
                      }

                      setSelectedIds(nextSelected);
                      setDraggingId(n.id);

                      setDragOrigin({
                        nodeId: n.id,
                        x: coords.x,
                        y: coords.y
                      });
                    }} 
                  style={{ cursor: "pointer" }}>
                    {/* Pulse Effect */}
                    {isCritical && <circle cx={n.position.x} cy={n.position.y} r={22} fill="transparent" stroke={statusColor} className="pulse-critical" />}
                    
                    <circle cx={n.position.x} cy={n.position.y} r={20} fill="#111" stroke={selectedId === n.id ? "#fff" : statusColor} strokeWidth="2" />
                    <text x={n.position.x} y={n.position.y + 5} textAnchor="middle" fill="#fff" fontSize="10px" fontWeight="bold">{n.id.split('-')[0]}</text>
                    <text x={n.position.x} y={n.position.y + 38} textAnchor="middle" fill="#888" fontSize="9px">{n.label}</text>
                  </g>
                );
              })}
            </g>
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="28" refY="5" orient="auto"><path d="M0,0 L10,5 L0,10 Z" fill="#333" /></marker>
            </defs>
          </svg>
          {/* UI Overlay: Zoom Percentage */}
          <div style={{ 
            position: "absolute", bottom: 10, left: 10, fontSize: "10px", 
            color: "#555", background: "rgba(0,0,0,0.5)", padding: "2px 6px", borderRadius: "3px" 
          }}>
            ZOOM: {(zoom * 100).toFixed(0)}% | X: {mousePos.x.toFixed(0)} Y: {mousePos.y.toFixed(0)}
          </div>
        </div>

        {/* RIGHT: INSPECTOR */}
        <div style={{ width: "300px", borderLeft: "1px solid #333", background: "var(--bg-sidebar)", padding: "15px", overflowY: "auto" }}>
           <div style={{ fontSize: "10px", color: "var(--accent-secondary)", fontWeight: "bold", marginBottom: "15px" }}>PROPERTIES</div>
           {selectedId ? (
             <div className="inspector-card" style={{ background: "#111", padding: "15px", borderRadius: "8px", border: "1px solid #222" }}>
                <div style={{ fontSize: "14px", fontWeight: "bold", color: "var(--accent)" }}>{selectedId}</div>
                <div style={{ fontSize: "11px", color: "#555", marginBottom: "15px" }}>Type: {nodes.find(n => n.id === selectedId)?.type}</div>
                
                <label style={{ fontSize: "10px", color: "#666" }}>SEMANTIC STATUS</label>
                <div style={{ color: getStatusColor(nodes.find(n => n.id === selectedId)?.semantic?.status), fontSize: "12px", marginBottom: "15px" }}>
                  ● {nodes.find(n => n.id === selectedId)?.semantic?.status?.toUpperCase()}
                </div>
                
                <pre style={{ fontSize: "10px", background: "#050505", padding: "10px", color: "#aaa", borderRadius: "4px", border: "1px solid #222" }}>
                  {JSON.stringify(nodes.find(n => n.id === selectedId)?.semantic, null, 2)}
                </pre>
             </div>
           ) : <div style={{ textAlign: "center", color: "#444", marginTop: "50px", fontSize: "12px" }}>Select a node to inspect</div>}
        </div>
      </div>
    </div>
  );
}
